import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '@app/store';
import * as DriverActions from '@app/store/driver/actions/driver.actions';
import * as driverStateSelectors from '@app/store/driver/selectors/driver.selector';
import { map } from 'rxjs/operators';
import { getDateTimeText, toTitleCase } from '@app/modules/shared/utilities/utilities';
import { DriverAsset, DriverProfile } from '@app/modules/driver/models/driver.models';
import { Observable, of } from 'rxjs';
import { ViewableAsset } from '@app/modules/location/models/viewable-asset.model';

@Injectable({
  providedIn: 'root'
})
export class DriverFacade {
  constructor(private store: Store<AppState>) {}

  unknownSourceText = 'Unknown Source'; // ZTT-2920 to translate
  unknownTsText = 'Unknown Time'; // ZTT-2920 to translate

  setDriverProfileById(driverProfileId: string): void {
    this.store.dispatch(DriverActions.loadDriverProfile({ driverProfileId }));
  }

  getDriverProfile() {
    return this.store.select(driverStateSelectors.selectDriverProfile);
  }

  getDriverProfileLoadState() {
    return this.store.select(driverStateSelectors.selectDriverProfileLoadState);
  }

  clearDriverProfile(): void {
    return this.store.dispatch(DriverActions.clearDriverProfile());
  }

  formatDriverAssociationText(driverChangeSource?: string): string {
    return driverChangeSource ? toTitleCase(driverChangeSource) : this.unknownSourceText;
  }
  getDriverEventTimestamp(driverEventTs?: string, dateTimeFormat = 'MM/DD/YYYY hh:mm:ss A'): string {
    return driverEventTs ? getDateTimeText(driverEventTs, dateTimeFormat) : this.unknownTsText;
  }

  getDriverAssociationSourceAndTime(asset: ViewableAsset, dateTimeFormat = 'MM/DD/YYYY hh:mm:ss A') {
    const driverChangeApplicationName = this.formatDriverAssociationText(asset.driverChangeSource);
    const dateTimeText = this.getDriverEventTimestamp(asset.driverEventTs, dateTimeFormat);
    return of(`${driverChangeApplicationName} at ${dateTimeText}`);
  }

  getDriverAssetProfileLoadState() {
    return this.store.select(driverStateSelectors.selectDriverAssetProfileLoadState);
  }

  getDriverAssetProfile(): Observable<DriverProfile[]> {
    return this.store.select(driverStateSelectors.selectDriverAssetProfile);
  }

  private parseTimestampToEpochMillis(timestamp?: string): number | undefined {
    if (!timestamp) return undefined;
    const epoch = Date.parse(timestamp);
    return Number.isNaN(epoch) ? undefined : epoch;
  }

  /**
   * Checks against each driver association in store, and keeps the most recent applicable
   * association event, per driver, for the trip period determined by start/end timestamps.
   * @param startTimestamp Timestamp for the beginning of the Trip
   * @param endTimestamp Timestamp for the end of the Trip
   */
  getDriverAssetProfilesForTrip(startTimestamp: string, endTimestamp: string): Observable<DriverProfile[]> {
    return this.store.select(driverStateSelectors.selectDriverAssetProfile).pipe(
      map(profiles => {
        const tripStart = this.parseTimestampToEpochMillis(startTimestamp);
        const tripEnd = this.parseTimestampToEpochMillis(endTimestamp);
        if (tripStart === undefined || tripEnd === undefined) return [];

        let profilesForTrip = [...profiles];

        // sort into reverse chronological order so dedupe keeps the most recent association per driver
        profilesForTrip.sort((a, b) => {
          const aStart = this.parseTimestampToEpochMillis(a.startTimestamp) ?? Number.NEGATIVE_INFINITY;
          const bStart = this.parseTimestampToEpochMillis(b.startTimestamp) ?? Number.NEGATIVE_INFINITY;
          return bStart - aStart;
        });

        // keep associations that overlap the trip time window
        profilesForTrip = profilesForTrip.filter(profile => {
          const associationStart = this.parseTimestampToEpochMillis(profile.startTimestamp);
          if (associationStart === undefined) return false;

          const associationEnd = this.parseTimestampToEpochMillis(profile.endTimestamp);
          const startsBeforeTripEnds = associationStart <= tripEnd;
          const endsAfterTripStarts = associationEnd === undefined || associationEnd > tripStart;
          return startsBeforeTripEnds && endsAfterTripStarts;
        });

        // keep only one association entry per driver id (most recent, based on previous reverse-chronological sort)
        const seenDriverIds = new Set<string>();
        profilesForTrip = profilesForTrip.filter(profile => {
          if (seenDriverIds.has(profile.id)) return false;
          seenDriverIds.add(profile.id);
          return true;
        });

        // return chronological order for display
        profilesForTrip.reverse();
        return profilesForTrip;
      })
    );
  }

  /**
   * Historical name retained for compatibility. Filters profiles to those active at a specific timestamp,
   * then sorts them in reverse-chronological order by startTimestamp.
   * @param profiles Array of DriverProfiles
   * @param timestamp Timestamp to match profile activity
   */
  sortProfilesForDay(profiles: DriverProfile[], timestamp: string): DriverProfile[] {
    const ts = this.parseTimestampToEpochMillis(timestamp);
    if (ts === undefined) return [];

    return [...profiles]
      .filter(profile => {
        const start = this.parseTimestampToEpochMillis(profile.startTimestamp);
        if (start === undefined) return false;
        const end = this.parseTimestampToEpochMillis(profile.endTimestamp);
        return start <= ts && (end === undefined || end > ts);
      })
      .sort((a, b) => {
        const aStart = this.parseTimestampToEpochMillis(a.startTimestamp) ?? Number.NEGATIVE_INFINITY;
        const bStart = this.parseTimestampToEpochMillis(b.startTimestamp) ?? Number.NEGATIVE_INFINITY;
        return bStart - aStart;
      });
  }

  /**
   * Takes a reverse-chronological sorted array of DriverProfiles and returns the Profile
   * which was the most recently associated prior to the provided timestamp
   * @param profiles Array of DriverProfiles
   * @param timestamp Timestamp to match the driver against
   */
  getDriverProfileAtTimestamp(profiles: DriverProfile[], timestamp: string): DriverProfile | undefined {
    const ts = this.parseTimestampToEpochMillis(timestamp);
    if (ts === undefined) return undefined;

    return profiles.find(profile => {
      const start = this.parseTimestampToEpochMillis(profile.startTimestamp);
      if (start === undefined || start > ts) return false;

      const end = this.parseTimestampToEpochMillis(profile.endTimestamp);
      return end === undefined || end > ts;
    });
  }

  setDriverAssetProfile(asset: ViewableAsset): void {
    this.store.dispatch(DriverActions.loadDriverAssetProfile({ asset }));
  }

  setDriverAssetProfileDirect(driverAssetProfile: DriverAsset[]): void {
    this.store.dispatch(DriverActions.loadDriverAssetProfileSuccess({ driverAssetProfile }));
  }

  setDriverAssetProfileError(driverAssetProfileFailure: unknown): void {
    this.store.dispatch(DriverActions.loadDriverAssetProfileFailure({ driverAssetProfileFailure }));
  }

  getDriverAssociationSrcTsText(driverProfile: DriverProfile) {
    const source = driverProfile.sourceLabel ? driverProfile.sourceLabel : driverProfile.source;
    const driverChangeApplicationName = this.formatDriverAssociationText(source);
    const dateTimeText = this.getDriverEventTimestamp(driverProfile.startTimestamp);
    return `${driverChangeApplicationName} at ${dateTimeText}`;
  }
}
